import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useI18n } from '../context/I18nContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Rocket, Sparkles, Layers, Zap, CheckCircle2, Clock, AlertCircle, XCircle } from 'lucide-react';
import { API } from '../config';
import axios from 'axios';

const changelogData = {
  en: [
    { version: 'v0.3.0', date: 'March 2026', tag: 'Current', items: ['Multilingual support (13 languages)', 'Dark/Light theme toggle', 'Downloads page', 'Hexagonal design system'] },
    { version: 'v0.2.0', date: 'February 2026', items: ['Plugin marketplace', 'Blog with AI generation', 'In-app notifications', 'Referral system with tiers'] },
    { version: 'v0.1.0', date: 'January 2026', items: ['Landing page launch', 'Waitlist with email confirmation', 'Stripe payment integration', 'Device licensing system', 'Google OAuth login'] },
  ],
  fr: [
    { version: 'v0.3.0', date: 'Mars 2026', tag: 'Actuel', items: ['Support multilingue (13 langues)', 'Thème sombre/clair', 'Page téléchargements', 'Système de design hexagonal'] },
    { version: 'v0.2.0', date: 'Février 2026', items: ['Marketplace de plugins', 'Blog avec génération IA', 'Notifications in-app', 'Système de parrainage à paliers'] },
    { version: 'v0.1.0', date: 'Janvier 2026', items: ['Lancement de la landing page', 'Liste d\'attente avec confirmation email', 'Intégration paiement Stripe', 'Système de licences par appareil', 'Connexion Google OAuth'] },
  ],
  hi: [
    { version: 'v0.3.0', date: 'मार्च 2026', tag: 'वर्तमान', items: ['बहुभाषी समर्थन (13 भाषाएँ)', 'डार्क/लाइट थीम टॉगल', 'डाउनलोड पृष्ठ', 'हेक्सागोनल डिज़ाइन सिस्टम'] },
    { version: 'v0.2.0', date: 'फरवरी 2026', items: ['प्लगइन मार्केटप्लेस', 'AI जनरेशन के साथ ब्लॉग', 'इन-ऐप नोटिफिकेशन', 'स्तरीय रेफरल सिस्टम'] },
    { version: 'v0.1.0', date: 'जनवरी 2026', items: ['लैंडिंग पेज लॉन्च', 'ईमेल पुष्टि के साथ प्रतीक्षा सूची', 'Stripe भुगतान इंटीग्रेशन', 'डिवाइस लाइसेंसिंग सिस्टम', 'Google OAuth लॉगिन'] },
  ],
};

const sectionLabels = {
  en: { changelog: 'Changelog', changelogSub: 'What\'s new in HexoPDF', roadmap: 'Roadmap', roadmapSub: 'What we\'re working on' },
  fr: { changelog: 'Changelog', changelogSub: 'Les nouveautés de HexoPDF', roadmap: 'Feuille de route', roadmapSub: 'Ce sur quoi nous travaillons' },
  es: { changelog: 'Historial', changelogSub: 'Novedades de HexoPDF', roadmap: 'Hoja de ruta', roadmapSub: 'En lo que estamos trabajando' },
  de: { changelog: 'Änderungsprotokoll', changelogSub: 'Was ist neu in HexoPDF', roadmap: 'Roadmap', roadmapSub: 'Woran wir arbeiten' },
  it: { changelog: 'Registro modifiche', changelogSub: 'Novità di HexoPDF', roadmap: 'Roadmap', roadmapSub: 'Su cosa stiamo lavorando' },
  pt: { changelog: 'Registro de alterações', changelogSub: 'Novidades do HexoPDF', roadmap: 'Roteiro', roadmapSub: 'No que estamos trabalhando' },
  nl: { changelog: 'Wijzigingslog', changelogSub: 'Wat is er nieuw in HexoPDF', roadmap: 'Roadmap', roadmapSub: 'Waar we aan werken' },
  pl: { changelog: 'Dziennik zmian', changelogSub: 'Co nowego w HexoPDF', roadmap: 'Plan', roadmapSub: 'Nad czym pracujemy' },
  tr: { changelog: 'Değişiklik günlüğü', changelogSub: 'HexoPDF\'teki yenilikler', roadmap: 'Yol Haritası', roadmapSub: 'Üzerinde çalıştıklarımız' },
  ru: { changelog: 'Журнал изменений', changelogSub: 'Что нового в HexoPDF', roadmap: 'План', roadmapSub: 'Над чем мы работаем' },
  ja: { changelog: '変更履歴', changelogSub: 'HexoPDFの最新情報', roadmap: 'ロードマップ', roadmapSub: '現在取り組んでいること' },
  zh: { changelog: '更新日志', changelogSub: 'HexoPDF的最新动态', roadmap: '路线图', roadmapSub: '我们正在做什么' },
  ar: { changelog: 'سجل التغييرات', changelogSub: 'ما الجديد في HexoPDF', roadmap: 'خارطة الطريق', roadmapSub: 'ما نعمل عليه' },
  hi: { changelog: 'चेंजलॉग', changelogSub: 'HexoPDF में नया क्या है', roadmap: 'रोडमैप', roadmapSub: 'हम किस पर काम कर रहे हैं' },
};

const statusLabels = {
  fr: { planned: 'Planifié', in_progress: 'En cours', completed: 'Terminé', cancelled: 'Annulé' },
  en: { planned: 'Planned', in_progress: 'In Progress', completed: 'Completed', cancelled: 'Cancelled' },
  es: { planned: 'Planificado', in_progress: 'En progreso', completed: 'Completado', cancelled: 'Cancelado' },
  de: { planned: 'Geplant', in_progress: 'In Arbeit', completed: 'Abgeschlossen', cancelled: 'Storniert' },
  it: { planned: 'Pianificato', in_progress: 'In corso', completed: 'Completato', cancelled: 'Annullato' },
  pt: { planned: 'Planejado', in_progress: 'Em andamento', completed: 'Concluído', cancelled: 'Cancelado' },
  nl: { planned: 'Gepland', in_progress: 'Bezig', completed: 'Voltooid', cancelled: 'Geannuleerd' },
  pl: { planned: 'Zaplanowane', in_progress: 'W toku', completed: 'Zakończone', cancelled: 'Anulowane' },
  tr: { planned: 'Planlanmış', in_progress: 'Devam ediyor', completed: 'Tamamlandı', cancelled: 'İptal edildi' },
  ru: { planned: 'Запланировано', in_progress: 'В процессе', completed: 'Завершено', cancelled: 'Отменено' },
  ja: { planned: '計画中', in_progress: '進行中', completed: '完了', cancelled: 'キャンセル' },
  zh: { planned: '已计划', in_progress: '进行中', completed: '已完成', cancelled: '已取消' },
  ar: { planned: 'مخطط', in_progress: 'قيد التنفيذ', completed: 'مكتمل', cancelled: 'ملغى' },
  hi: { planned: 'नियोजित', in_progress: 'प्रगति में', completed: 'पूर्ण', cancelled: 'रद्द' },
};

const statusConfig = {
  planned: { icon: Clock, color: 'text-muted-foreground', bg: 'bg-secondary', border: 'border-border', dot: 'bg-muted-foreground' },
  in_progress: { icon: Sparkles, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-500/5', border: 'border-blue-500/30', dot: 'bg-blue-500' },
  completed: { icon: CheckCircle2, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500/5', border: 'border-emerald-500/30', dot: 'bg-emerald-500' },
  cancelled: { icon: XCircle, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-500/5', border: 'border-red-500/30', dot: 'bg-red-500' },
};

const categoryI18n = {
  fr: { feature: 'Fonctionnalité', improvement: 'Amélioration', bugfix: 'Correction', integration: 'Intégration' },
  en: { feature: 'Feature', improvement: 'Improvement', bugfix: 'Bugfix', integration: 'Integration' },
  es: { feature: 'Función', improvement: 'Mejora', bugfix: 'Corrección', integration: 'Integración' },
  de: { feature: 'Feature', improvement: 'Verbesserung', bugfix: 'Bugfix', integration: 'Integration' },
  it: { feature: 'Funzionalità', improvement: 'Miglioramento', bugfix: 'Correzione', integration: 'Integrazione' },
  pt: { feature: 'Recurso', improvement: 'Melhoria', bugfix: 'Correção', integration: 'Integração' },
  nl: { feature: 'Functie', improvement: 'Verbetering', bugfix: 'Bugfix', integration: 'Integratie' },
  pl: { feature: 'Funkcja', improvement: 'Ulepszenie', bugfix: 'Poprawka', integration: 'Integracja' },
  tr: { feature: 'Özellik', improvement: 'İyileştirme', bugfix: 'Hata düzeltme', integration: 'Entegrasyon' },
  ru: { feature: 'Функция', improvement: 'Улучшение', bugfix: 'Исправление', integration: 'Интеграция' },
  ja: { feature: '機能', improvement: '改善', bugfix: 'バグ修正', integration: '統合' },
  zh: { feature: '功能', improvement: '改进', bugfix: '修复', integration: '集成' },
  ar: { feature: 'ميزة', improvement: 'تحسين', bugfix: 'إصلاح', integration: 'تكامل' },
  hi: { feature: 'सुविधा', improvement: 'सुधार', bugfix: 'बग फिक्स', integration: 'इंटीग्रेशन' },
};

export default function ChangelogPage() {
  const { t, lang } = useI18n();
  const labels = sectionLabels[lang] || sectionLabels.en;
  const changelog = changelogData[lang] || changelogData.en;
  const sLabels = statusLabels[lang] || statusLabels.en;
  const catLabels = categoryI18n[lang] || categoryI18n.en;
  const [roadmapItems, setRoadmapItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiChangelog, setApiChangelog] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/roadmap`).then(r => setRoadmapItems(r.data.items || [])).catch(() => {}),
      axios.get(`${API}/changelog`).then(r => setApiChangelog(r.data.entries || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const displayChangelog = apiChangelog.length > 0 ? apiChangelog : changelog;

  const groupedByStatus = ['in_progress', 'planned', 'completed', 'cancelled'].reduce((acc, status) => {
    const items = roadmapItems.filter(item => item.status === status);
    if (items.length > 0) acc.push({ status, items });
    return acc;
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="changelog-page">
      <Navbar />
      <section className="pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          {/* Changelog */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-2" data-testid="changelog-title">
              {labels.changelog}
            </h1>
            <p className="text-muted-foreground text-base md:text-lg">{labels.changelogSub}</p>
          </motion.div>

          <div className="relative mb-20">
            <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
            <div className="space-y-8">
              {displayChangelog.map((entry, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -15 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="relative pl-14"
                  data-testid={`changelog-entry-${i}`}
                >
                  <div className="absolute left-2 top-1 w-5 h-5 rounded-full bg-blue-500 border-4 border-background shadow-[0_0_8px_rgba(59,130,246,0.4)]" />
                  <div className="p-5 rounded-xl bg-card border border-border">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <span className="font-heading font-bold text-foreground text-lg">{entry.version}</span>
                      <span className="text-muted-foreground text-xs">{entry.date}</span>
                      {entry.tag && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                          {entry.tag}
                        </span>
                      )}
                    </div>
                    <ul className="space-y-1.5">
                      {entry.items.map((item, j) => (
                        <li key={j} className="text-sm text-muted-foreground flex items-start gap-2">
                          <Zap className="w-3 h-3 text-blue-500 mt-1 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Roadmap (Dynamic from API) */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-6">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-2" data-testid="changelog-roadmap-title">
              {labels.roadmap}
            </h2>
            <p className="text-muted-foreground text-base">{labels.roadmapSub}</p>
          </motion.div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : roadmapItems.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-8" data-testid="roadmap-empty">
              {labels.roadmapSub}
            </p>
          ) : (
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
              <div className="space-y-8">
                {groupedByStatus.map(({ status, items }, gi) => {
                  const cfg = statusConfig[status] || statusConfig.planned;
                  const Icon = cfg.icon;
                  return (
                    <motion.div
                      key={status}
                      initial={{ opacity: 0, x: -15 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: gi * 0.1 }}
                      className="relative pl-14"
                      data-testid={`roadmap-group-${status}`}
                    >
                      <div className={`absolute left-2 top-1 w-5 h-5 rounded-full ${cfg.dot} border-4 border-background`} />
                      <div className={`p-5 rounded-xl ${cfg.bg} border ${cfg.border}`}>
                        <div className="flex items-center gap-2 mb-4">
                          <Icon className={`w-4 h-4 ${cfg.color}`} />
                          <span className={`text-xs uppercase tracking-wider ${cfg.color} font-medium`}>
                            {sLabels[status] || status} ({items.length})
                          </span>
                        </div>
                        <div className="space-y-3">
                          {items.map((item, j) => (
                            <div key={item.id || j} className="flex items-start gap-3" data-testid={`roadmap-item-${item.id}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} mt-2 shrink-0`} />
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-foreground text-sm font-medium">{item.title}</span>
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border">
                                    {catLabels[item.category] || item.category}
                                  </span>
                                </div>
                                {item.description && (
                                  <p className="text-muted-foreground text-xs mt-0.5">{item.description}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
